<template>
  <div class="grid w-full items-center gap-1.5">
    <form @submit="onSubmit">
      <Label for="email" class="text-colorGrey4 font-bold">{{
        t("signup.email")
      }}</Label>
      <FormField v-slot="{ componentField }" name="email">
        <FormItem>
          <FormControl>
            <Input
              v-bind="componentField"
              v-model="email"
              class="w-full text-white h-10 focus-visible:ring-0"
              type="text"
            />
          </FormControl>
          <FormMessage class="font-normal text-xs" />
        </FormItem>
      </FormField>
      
      <Button
        class="w-full mt-5 h-11"
        type="button"
        :disabled="buttonDisabled"
        @click="handleStartVerification"
      >
        {{ t("signup.verifyEmail") }}
      </Button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { z } from "zod";
import { useI18n } from "vue-i18n";
import Button from "@/components/ui/button/Button.vue";
import Label from "@/components/ui/label/Label.vue";
import Input from "@/components/ui/input/Input.vue";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

const { t } = useI18n();
const config = useRuntimeConfig();

const email = defineModel<string>('email', { required: true });

const emit = defineEmits<{
  startVerification: []
  startVerificationWithError: [errorMessage: string]
}>();

interface APIError {
  data: {
    result: boolean;
    code: number; 
    message: string;
  }
}

const schema = toTypedSchema(
  z.object({
    email: z.string({ required_error: t('signup.emailIsRequired')}).min(1, t('signup.emailIsRequired')).email(t("signup.invalidEmail")),
  })
);

const { handleSubmit, values, setFieldError } = useForm<{ email: string }>({
  validationSchema: schema,
});

const onSubmit = handleSubmit((_values: any) => {
  // 表單提交邏輯，如果需要的話
});

const handleStartVerification = async () => {
  try {
    await $fetch(`${config.public.APIurl}/api/v3/register/email/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: { 
        email: email.value, 
      }
    });
    // API 成功，通知父組件
    emit('startVerification');
  } catch(error: unknown) {
    const err = error as APIError;
    console.error(err.data.message);
    if(err.data.code === 12006) {
      // 只有 user exist 錯誤才在此組件顯示
      setFieldError('email', err.data.message);
    } else {
      // 其他所有錯誤都跳轉到 verifyEmail 組件，並傳遞錯誤訊息
      const errorMessage = err.data.code === 17006 ? t('17006') : 'Something went wrong';
      emit('startVerificationWithError', errorMessage);
    }
  }
};

const buttonDisabled = computed(() => {
  return import.meta.server || !values.email;
});
</script>
